import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Download, ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { createCheckoutSession } from "@/lib/api";
import { toast } from "sonner";

export default function BundleUpsell({ analysisId }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onUpgrade = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Vul een geldig e-mailadres in.");
      return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const result = await createCheckoutSession({
        analysis_id: analysisId,
        package: "bundle",
        email: email.trim(),
        origin_url: origin,
      });
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Er ging iets mis";
      toast.error(`Checkout mislukt: ${msg}`);
      setLoading(false);
    }
  };

  return (
    <section
      id="bundle-upsell"
      className="mt-12 bg-gradient-to-br from-blue-700 to-blue-900 rounded-2xl p-6 sm:p-10 text-white shadow-xl overflow-hidden relative"
      data-testid="bundle-upsell"
    >
      <div aria-hidden className="absolute -top-10 -right-10 h-48 w-48 rounded-full bg-amber-400/20 blur-3xl" />
      <div className="relative grid lg:grid-cols-5 gap-8 items-center">
        <div className="lg:col-span-3">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/15 border border-amber-300/30 text-amber-200 text-xs font-bold uppercase tracking-widest">
            <Sparkles className="h-3.5 w-3.5" />
            Upgrade naar brievenbundel
          </div>
          <h3 className="mt-4 font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            Download alle 4 brieven voor maar <span className="text-amber-300">€13 extra</span>
          </h3>
          <p className="mt-3 text-blue-100 leading-relaxed">
            Opzegbrief, klachtbrief, bezwaarbrief en meterstand-correctie — kant-en-klaar gegenereerd
            op basis van JOUW analyse. Direct downloadbaar als PDF.
          </p>
          <ul className="mt-5 grid grid-cols-2 gap-2 text-sm">
            {["Opzegbrief PDF", "Klachtbrief PDF", "Bezwaarbrief factuur PDF", "Meterstand correctie PDF"].map((x, i) => (
              <li key={i} className="flex items-center gap-2 text-blue-50">
                <Download className="h-4 w-4 text-amber-300" /> {x}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-2">
          <form onSubmit={onUpgrade} className="bg-white text-slate-900 rounded-2xl p-5 shadow-2xl" data-testid="bundle-form">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Brievenbundel</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-heading text-3xl font-bold">€19,95</span>
              <span className="text-sm text-slate-500">€13 extra t.o.v. rapport</span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Eenmalige betaling · veilig via Stripe</p>

            <label htmlFor="bundle-email" className="mt-4 block text-xs font-semibold text-slate-700">
              E-mail (voor bevestiging)
            </label>
            <Input
              id="bundle-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jij@voorbeeld.nl"
              className="mt-1"
              data-testid="bundle-email-input"
              required
            />

            <button
              type="submit"
              disabled={loading}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold rounded-lg px-4 py-3 transition-all disabled:opacity-50"
              data-testid="bundle-upgrade-btn"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Doorgaan...</>
              ) : (
                <>Ontgrendel brievenbundel<ArrowRight className="h-4 w-4" /></>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
